import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getToken, parseJwt } from 'utils/common/session';
import * as services from 'services/login';
import { isAuthed } from 'utils/common/session';

import { useCallbackRef } from 'hooks/useCallbackRef';

export const userInfoKey = (userId, companyId) => [
  'user-info',
  parseInt(userId),
  parseInt(companyId),
];

// Handy extractor — compute the key from whatever token is current
export function getUserInfoKeyFromToken(token) {
  const { UserId, CompanyId } = parseJwt(token) || {};
  if (!UserId || !CompanyId) return null;
  return userInfoKey(UserId, CompanyId);
}

export const useUserInfo = (options = {}) => {
  const accessToken = getToken();
  const queryClient = useQueryClient();
  const queryKey = getUserInfoKeyFromToken(accessToken);

  const {
    data: userInfo,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKey ?? ['user-info', 'anon'],
    queryFn: async () => {
      const res = await services.getUserInfo();
      return res?.data ?? null;
    },
    enabled: !!accessToken && !!isAuthed(),
    ...options,
  });

  return {
    userInfo: userInfo ?? {},
    userId: userInfo?.id,
    memberId: userInfo?.member?.id,
    userLoading: isLoading,
    updateUserInfo: useCallbackRef(() => {
      queryClient.invalidateQueries({
        queryKey,
      });
    }),
    error,
  };
};

export const clearUserInfoQuery = (queryClient) => {
  queryClient.removeQueries({
    predicate: (q) =>
      Array.isArray(q.queryKey) && q.queryKey[0] === 'user-info',
  });
};

export function useRefreshUserInfo() {
  const qc = useQueryClient();
  return () =>
    qc.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) && q.queryKey[0] === 'user-info',
    });
}

export function useSetUserInfo() {
  const qc = useQueryClient();

  return (data) => {
    const accessToken = getToken();
    const k = getUserInfoKeyFromToken(accessToken);
    if (k) qc.setQueryData(k, data);
  };
}

export const queryUserInfo = (queryClient) => {
  const accessToken = getToken();
  const queryKey = getUserInfoKeyFromToken(accessToken);

  return queryClient.ensureQueryData({
    queryKey,
    queryFn: async () => {
      const res = await services.getUserInfo();
      return res?.data ?? null;
    },
  });
};

export const prefetchUserInfo = async (queryClient, access_token) => {
  const key = getUserInfoKeyFromToken(access_token);
  await queryClient.prefetchQuery({
    queryKey: key ?? ['user-info', 'anon'],
    queryFn: async () => {
      const res = await services.getUserInfo();
      return res?.data ?? null;
    },
  });
};
